import React, { useMemo } from "react";
import { Box, Chip, Typography } from "@mui/material";
import { PieChart, Pie, Cell } from "recharts";
import { MimshakData } from "../../interfaces/DTO/mimshakData";
import { MimshakGroupByService } from "../../services/MimshakGroupByService";
import { colorProductTypeUniversal } from "../../utils/colorEnumFormatter";
import { formatCurrency } from "../../utils/namesFormater";
import { formatProductTypeUniversal } from "../../utils/productTypeUniversalFormatter";

interface Props {
  data: MimshakData[];
  /** ערך לסיכום לכל פוליסה — ברירת מחדל funds_sum */
  getValue?: (item: MimshakData) => number;
  /** תצוגת הערך בשורה — ברירת מחדל סכום בש"ח */
  formatValue?: (productType: number, value: number) => React.ReactNode;
  selectedProductType?: number | null;
  onProductTypeFilter: (productType: number | null) => void;
}

const metricRowSx = {
  color: "#6d1727",
  fontWeight: 500,
};

const GroupByAdjustedProductTypePieChartWithList: React.FC<Props> = ({
  data,
  getValue,
  formatValue = (_, value) => formatCurrency(value),
  selectedProductType,
  onProductTypeFilter,
}) => {
  const entries = useMemo(
    () =>
      Object.entries(MimshakGroupByService.groupByProductTypeAdjustingPureSavings(data, getValue))
        .map(([type, value]) => [Number(type), value] as [number, number])
        .filter(([, v]) => v > 0)
        .sort(([, a], [, b]) => b - a),
    [data, getValue]
  );

  const total = useMemo(() => entries.reduce((sum, [, v]) => sum + v, 0), [entries]);

  if (entries.length === 0) return null;

  const pieData = entries.map(([type, value]) => ({ name: type, value }));
  const hasSelection = selectedProductType !== null && selectedProductType !== undefined;

  const handleRowClick = (type: number) => {
    onProductTypeFilter(selectedProductType === type ? null : type);
  };

  return (
    <Box sx={{ textAlign: "center" }}>
      <PieChart width={120} height={120} style={{ margin: "0 auto" }}>
        <Pie
          data={pieData}
          dataKey="value"
          cx={55}
          cy={55}
          outerRadius={55}
          startAngle={90}
          endAngle={-270}
        >
          {pieData.map((entry) => (
            <Cell
              key={entry.name}
              fill={colorProductTypeUniversal(Number(entry.name))}
              stroke="#fff"
              strokeWidth={hasSelection && selectedProductType !== entry.name ? 0 : 4}
              opacity={hasSelection && selectedProductType !== entry.name ? 0.35 : 1}
            />
          ))}
        </Pie>
      </PieChart>

      <Box sx={{ mt: 1, display: "table", width: "85%", mx: "auto" }}>
        {entries.map(([type, value]) => {
          const pct = total > 0 ? Math.round((value / total) * 100) : 0;
          const color = colorProductTypeUniversal(type);
          const isSelected = selectedProductType === type;
          const isDimmed = hasSelection && !isSelected;

          return (
            <Box
              key={type}
              onClick={() => handleRowClick(type)}
              sx={{
                display: "table-row",
                cursor: "pointer",
                opacity: isDimmed ? 0.4 : 1,
                bgcolor: isSelected ? `${color}18` : "transparent",
                borderRadius: 1,
                transition: "opacity 0.15s",
                "&:hover": { opacity: 1 },
              }}
            >
              <Box sx={{ display: "table-cell", verticalAlign: "middle", textAlign: "right" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "flex-end", flexDirection: "row-reverse" }}>
                  <Typography variant="body1" sx={{ ...metricRowSx, textAlign: "right", fontWeight: isSelected ? 700 : 500 }}>
                    {formatProductTypeUniversal(type)}
                  </Typography>
                  <Chip
                    label={`${pct}%`}
                    size="small"
                    sx={{ bgcolor: color, color: "#fff", fontWeight: 700, borderRadius: "6px", mb: "5px", flexShrink: 0 }}
                  />
                </Box>
              </Box>
              <Typography
                variant="body1"
                sx={{ ...metricRowSx, display: "table-cell", textAlign: "right", minWidth: "100px", whiteSpace: "nowrap", fontWeight: isSelected ? 700 : 500 }}
              >
                {formatValue(type, value)}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {hasSelection && (
        <Box
          onClick={() => onProductTypeFilter(null)}
          sx={{ mt: 1, cursor: "pointer", fontSize: 12, color: "text.secondary", textDecoration: "underline" }}
        >
          נקה פילטר
        </Box>
      )}
    </Box>
  );
};

export default GroupByAdjustedProductTypePieChartWithList;
